/**
 * GST invoice / credit-note numbering.
 * Rules (CGST Rule 46): consecutive, unique within the financial year, max 16 characters,
 * only letters, digits, "-" and "/".
 *
 * Format tokens:
 *   {OUTLET}  outlet code (2–4 chars)        {DEVICE}  device code (D001…, offline series)
 *   {FY}      financial year, e.g. 2627      {FY2}     start year, e.g. 26
 *   {SEQ}     sequence, no padding           {SEQn}    sequence zero-padded to n digits (n = 1–9)
 * Anything else must be literal [A-Za-z0-9/-].
 */
export const INVOICE_NUMBER_RE = /^[A-Za-z0-9/-]{1,16}$/;
const TOKEN_RE = /\{(OUTLET|DEVICE|FY2|FY|SEQ\d?)\}/g;

const TOKEN_MAX_LEN = { OUTLET: 4, DEVICE: 4, FY: 4, FY2: 2 };

function parts(format) {
  const out = [];
  let last = 0;
  for (const m of format.matchAll(TOKEN_RE)) {
    if (m.index > last) out.push({ literal: format.slice(last, m.index) });
    out.push({ token: m[1] });
    last = m.index + m[0].length;
  }
  if (last < format.length) out.push({ literal: format.slice(last) });
  return out;
}

/**
 * Check a format string. Returns { ok, error?, maxLength }.
 * maxLength assumes the longest outlet/device code and the padded sequence width (or 6 digits for {SEQ}).
 */
export function validateNumberFormat(format, { requireOutletOrDevice = true } = {}) {
  if (typeof format !== 'string' || !format.trim()) return { ok: false, error: 'Format is required' };
  const p = parts(format);
  const literals = p
    .filter((x) => x.literal)
    .map((x) => x.literal)
    .join('');
  if (/[^A-Za-z0-9/-]/.test(literals)) {
    return { ok: false, error: 'Only letters, digits, "-", "/" and {TOKENS} are allowed' };
  }
  const seq = p.filter((x) => x.token?.startsWith('SEQ'));
  if (seq.length !== 1) return { ok: false, error: 'Use exactly one {SEQ} or {SEQn} token' };
  if (requireOutletOrDevice && !p.some((x) => x.token === 'OUTLET' || x.token === 'DEVICE')) {
    return { ok: false, error: 'Include {OUTLET} (or {DEVICE}) so numbers are unique across outlets' };
  }
  if (!p.some((x) => x.token === 'FY' || x.token === 'FY2')) {
    return { ok: false, error: 'Include {FY} or {FY2}: numbering restarts every financial year' };
  }
  let maxLength = literals.length;
  for (const x of p.filter((y) => y.token)) {
    if (x.token.startsWith('SEQ')) maxLength += Number(x.token.slice(3)) || 6;
    else maxLength += TOKEN_MAX_LEN[x.token];
  }
  if (maxLength > 16)
    return { ok: false, error: `Numbers could be ${maxLength} characters long; GST allows at most 16`, maxLength };
  return { ok: true, maxLength };
}

/** Build a number. fy: { code: '2627', startYear: 2026 } (see time/financialYear). */
export function formatNumber(format, { outletCode = '', deviceCode = '', fy, seq }) {
  const out = format.replace(TOKEN_RE, (_m, token) => {
    if (token === 'OUTLET') return outletCode;
    if (token === 'DEVICE') return deviceCode;
    if (token === 'FY') return fy.code;
    if (token === 'FY2') return String(fy.startYear).slice(2);
    const pad = Number(token.slice(3)) || 0;
    return String(seq).padStart(pad, '0');
  });
  if (!INVOICE_NUMBER_RE.test(out)) {
    throw new Error(`Generated number "${out}" breaks GST rules (max 16 chars, letters/digits/-/ only)`);
  }
  return out;
}
